import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Link2, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";

export const ShareDialog = ({ open, onOpenChange, plan, setPlan }) => {
  const [friends, setFriends] = useState(null);
  const [link, setLink] = useState("");
  useEffect(() => {
    if (open) api.get("/friends").then((r) => setFriends(r.data.friends)).catch(() => setFriends([]));
  }, [open]);

  const has = (id) => plan.shared_with?.includes(id);
  const toggle = async (f) => {
    try {
      const { data } = has(f.id) ? await api.delete(`/plans/${plan.id}/share/${f.id}`) : await api.post(`/plans/${plan.id}/share`, { user_id: f.id });
      setPlan(data);
      toast.success(has(f.id) ? `${f.name} can no longer see this plan` : `${f.name} can now see this plan`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const showStart = async (v) => {
    const { data } = await api.post(`/plans/${plan.id}/privacy`, { show_start: v });
    setPlan(data);
  };
  const makeLink = async () => {
    const { data } = await api.post(`/plans/${plan.id}/invite`);
    const url = `${window.location.origin}/invite/${data.token}`;
    setLink(url);
    setPlan({ ...plan, status: plan.status === "draft" ? "saved" : plan.status });
    navigator.clipboard?.writeText(url).then(() => toast.success("Invite link copied"), () => {});
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[1250] sm:max-w-md" data-testid="share-modal">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2"><Lock className="w-4 h-4" /> Share with friends</DialogTitle>
          <DialogDescription>Private by default. Only friends you switch on can see this plan, and you can remove them anytime.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2" data-testid="share-friend-list">
          {friends?.length === 0 && <p className="text-sm text-stone-500">No friends yet. <Link to="/friends" className="text-[#23603F] font-semibold">Add a friend</Link> by email first.</p>}
          {friends?.map((f) => (
            <label key={f.id} className="flex items-center justify-between rounded-xl border border-stone-200 px-3 py-2.5">
              <span><span className="font-semibold text-sm">{f.name}</span><span className="block text-xs text-stone-500">{f.email}</span></span>
              <Switch data-testid={`friend-access-toggle-${f.email.split("@")[0]}`} checked={has(f.id)} onCheckedChange={() => toggle(f)} />
            </label>
          ))}
        </div>
        <label className="flex items-center justify-between gap-3 rounded-xl bg-stone-50 border border-stone-200 px-3 py-3">
          <span className="text-sm"><span className="font-semibold">Show my starting point</span><span className="block text-xs text-stone-500">Off: friends see the stops, never where you start.</span></span>
          <Switch data-testid="share-show-start-switch" checked={plan.show_start} onCheckedChange={showStart} />
        </label>
        <div className="rounded-xl border border-dashed border-stone-300 p-3 space-y-2">
          <p className="text-sm font-semibold flex items-center gap-1.5"><Link2 className="w-4 h-4" /> Invite link (asks for access)</p>
          <p className="text-xs text-stone-500">Anyone with the link can only <em>request</em> access. Nothing is visible until you approve them.</p>
          {link ? (
            <div className="flex gap-2"><input readOnly value={link} data-testid="share-invite-link" className="flex-1 min-w-0 text-xs font-mono h-9 rounded-lg border px-2 bg-white" />
              <button onClick={() => navigator.clipboard?.writeText(link)} className="px-2 rounded-lg border hover:bg-stone-100"><Copy className="w-4 h-4" /></button></div>
          ) : (
            <button data-testid="share-create-invite-btn" onClick={makeLink} className="text-sm font-semibold text-[#23603F] hover:underline">Create invite link</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
